import React, { useState } from "react";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import classNames from "classnames";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { resetPassword, sendPasswordRecoveryEmail, SSO_REQUIRED } from "../../../services/security-services/SecurityActions";
import { useDispatch } from "react-redux";
import { useTranslation } from 'react-i18next';

const schema = yup.object().shape({
  email: yup.string().email().required("Email is required"),
});

export default function ForgotPasswordForm() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const [submitted, setSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({ resolver: yupResolver(schema) });


  const onSubmit = async ({ email }) => {
    setIsLoading(true);
    try {
      const success = await dispatch(sendPasswordRecoveryEmail(email));

      if (success) {
        setSubmitted(true);
        toast.success(t('forgotPasswordForm.messages.sendingResetMail', { email }));
        setTimeout(() => navigate("/login"), 2000);
      } else {
        toast.error(t('forgotPasswordForm.messages.resetMailNotSent'));
      }
    } catch (err) {
      if (err?.response?.data?.code === SSO_REQUIRED) {
        toast.error(t('loginPage.errors.ssoRequired'));
      } else if (err?.response?.status === 403) {
        const message = err?.response?.data?.message || t('forgotPasswordForm.messages.accessForbidden');
        toast.error(message);
      } else {
        toast.error(t('forgotPasswordForm.messages.errorOccurred'));
      }
    } finally {
      setIsLoading(false);
    }
  };


  return (
    <form onSubmit={handleSubmit(onSubmit)} className="text-left">
      {/* Back link */}
      <div className="mb-3">
        <button
          type="button"
          onClick={() => navigate("/login")}
          className="btn btn-link p-0 text-decoration-none"
          style={{
            color: "#9333ea",
            fontWeight: 500,
            fontSize: "0.95rem",
            transition: "all 0.2s ease-in-out",
            display: "flex",
            alignItems: "center",
            gap: "0.4rem",
          }}
          onMouseOver={(e) => (e.currentTarget.style.color = "#7c3aed")}
          onMouseOut={(e) => (e.currentTarget.style.color = "#9333ea")}
        >
          <i className="fa fa-chevron-left" /> {t('forgotPasswordForm.backToLogin')}
        </button>
      </div>

      {/* Email field */}
      <div className="form-group mb-4">
        <label htmlFor="email" className="fw-medium mb-1">{t('forgotPasswordForm.email')}</label>
        <div
          className={classNames(
            "input-group border rounded shadow-sm overflow-hidden transition"
          )}
          style={{
            transition: "all 0.2s ease-in-out",
            boxShadow: errors.email ? "0 0 0 3px rgba(220, 53, 69, 0.25)" : "none",
          }}
        >
          <span className="input-group-text bg-white border-0">
            <i className="fa fa-envelope" />
          </span>
          <input
            type="email"
            id="email"
            placeholder={t('forgotPasswordForm.emailPlaceholder')}
            className={classNames("form-control border-0", {
              "is-invalid": errors.email,
            })}
            {...register("email")}
            onFocus={(e) =>
              e.target.parentNode.style.boxShadow = "0 0 0 3px rgba(124, 58, 237, 0.25)"
            }
            onBlur={(e) =>
              e.target.parentNode.style.boxShadow = errors.email
                ? "0 0 0 3px rgba(220, 53, 69, 0.25)"
                : "none"
            }
          />
        </div>
        <div className="invalid-feedback d-block">{errors.email?.message}</div>
      </div>

      {/* Submit button */}
      <button
        type="submit"
        disabled={submitted || isLoading}
        className="btn w-100 py-2 fw-semibold text-white"
        style={{
          background: "linear-gradient(90deg, #7c3aed, #2563eb)",
          border: "none",
          borderRadius: "0.6rem",
          transition: "all 0.2s ease-in-out",
          transform: isLoading ? "scale(0.98)" : "scale(1)",
          boxShadow: isLoading ? "none" : "0 4px 14px rgba(36, 99, 235, 0.25)",
        }}
        onMouseEnter={(e) => {
          if (!isLoading && !submitted) {
            e.currentTarget.style.transform = "translateY(-2px)";
            e.currentTarget.style.boxShadow = "0 6px 18px rgba(36, 99, 235, 0.35)";
          }
        }}
        onMouseLeave={(e) => {
          if (!isLoading && !submitted) {
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow = "0 4px 14px rgba(36, 99, 235, 0.25)";
          }
        }}
        onMouseDown={(e) => {
          if (!isLoading && !submitted) {
            e.currentTarget.style.transform = "scale(0.98)";
          }
        }}
        onMouseUp={(e) => {
          if (!isLoading && !submitted) {
            e.currentTarget.style.transform = "translateY(-2px)";
          }
        }}
      >
        {isLoading ? (
          <>
            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
            {t('forgotPasswordForm.sending')}
          </>
        ) : submitted ? (
          t('forgotPasswordForm.emailSent')
        ) : (
          t('forgotPasswordForm.resetPassword')
        )}
      </button>
    </form>
  );
}
